import React, { useEffect, useState } from 'react'
import { useEditor, EditorContent, FocusPosition, AnyExtension, Editor, JSONContent } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'
import Bold from '@tiptap/extension-bold'
import Italic from '@tiptap/extension-italic'
import Underline from '@tiptap/extension-underline'
import CharacterCount from '@tiptap/extension-character-count'
import { Bold as BoldIcon, Italic as ItalicIcon, Underline as UnderlineIcon } from 'react-feather'
import { classNames } from 'src/lib/utils/helpers'

interface RichTextEditorProps {
  value: string
  label?: string
  placeholder?: string
  handleTextChange: (content: string) => void
  errorStatus?: boolean
  errorMessage?: string
  handleFocus?: (e: any) => void
  charLimit?: number
  initialDataLoaded?: boolean
  labelCustomClass?: string
  forceAutoFocus?: FocusPosition
  groupName?: string
  customCSS?: React.CSSProperties
  disabled?: boolean
  showToolbar?: boolean
  showFormatting?: boolean
  /** Bullet lists and Enter for a new line; also drops the default character cap. */
  multiline?: boolean
  extraExtensions?: AnyExtension[]
  contentJson?: JSONContent
  onUpdateJson?: (editor: Editor) => void
  onEditorReady?: (editor: Editor | null) => void
  onKeyDown?: (event: KeyboardEvent) => boolean
}

const preProcessContent = (content: string) =>
  content
    .replace(/<code>/g, '<span>')
    .replace(/<\/code>/g, '</span>')
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/p>\s*<p[^>]*>/gi, ' ')
    .replace(/((<p[^>]*>)|^)\s*\*\s*/g, '$2')

const flattenPastedHTML = (html: string) => {
  const text = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim()
  return `<p>${text}</p>`
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  label,
  placeholder,
  handleTextChange,
  handleFocus,
  charLimit,
  initialDataLoaded,
  labelCustomClass = '',
  forceAutoFocus = false,
  groupName,
  customCSS,
  disabled,
  showToolbar = true,
  showFormatting = true,
  multiline = false,
  extraExtensions,
  contentJson,
  onUpdateJson,
  onEditorReady,
  onKeyDown
}) => {
  const [isFocused, setIsFocused] = useState(false)
  const limit = charLimit ?? (multiline ? null : 60)

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          heading: false,
          bulletList: multiline ? undefined : false,
          orderedList: false,
          listItem: multiline ? undefined : false,
          hardBreak: false
        }),
        ...(showFormatting ? [Bold, Italic, Underline] : []),
        CharacterCount.configure({ limit }),
        ...(extraExtensions ?? [])
      ],
      content: contentJson ?? preProcessContent(value),
      onUpdate: ({ editor }) => {
        if (onUpdateJson) {
          onUpdateJson(editor)
          return
        }

        const chars = editor.storage.characterCount?.characters() ?? editor.getText().length
        if (limit !== null && chars > limit) {
          const trimmed = editor.getText().slice(0, limit)
          editor.commands.setContent(`<p>${trimmed}</p>`, false)
          editor.commands.focus('end')
        }
        handleTextChange(editor.getHTML())
      },
      autofocus: forceAutoFocus ? 'end' : false,
      onFocus: (e: any) => {
        setIsFocused(true)
        if (handleFocus) handleFocus(e)
      },
      onBlur: () => {
        setIsFocused(false)
      },
      editorProps: {
        transformPastedText(text) {
          return text.replace(/[\r\n]+/g, ' ').replace(/\s+/g, ' ')
        },
        transformPastedHTML(html) {
          return flattenPastedHTML(html)
        },
        handleKeyDown(_view, event) {
          if (onKeyDown?.(event)) return true
          if (event.key === 'Enter' && !multiline) {
            event.preventDefault()
            return true
          }
          return false
        }
      }
    },
    [groupName, limit]
  )

  useEffect(() => {
    onEditorReady?.(editor)
  }, [editor, onEditorReady])

  useEffect(() => {
    if (contentJson) return
    if (initialDataLoaded && editor) {
      const nextContent = preProcessContent(value)
      if (editor.getHTML() !== nextContent) {
        editor.commands.setContent(nextContent, false, { preserveWhitespace: 'full' })
        if (forceAutoFocus) editor.commands.focus()
      }
    }
  }, [value, initialDataLoaded, editor, limit])

  useEffect(() => {
    if (forceAutoFocus && editor) {
      editor.commands.focus(forceAutoFocus)
    }
  }, [forceAutoFocus, editor])

  const isBoldActive = editor?.isActive('bold') ?? false
  const isUnderlineActive = editor?.isActive('underline') ?? false
  const isItalicActive = editor?.isActive('italic') ?? false

  const handleBold = () => editor?.chain().focus().toggleBold().run()
  const handleItalic = () => editor?.chain().focus().toggleItalic().run()
  const handleUnderline = () => editor?.chain().focus().toggleUnderline().run()

  const formattingButtons = !showFormatting
    ? []
    : [
        {
          id: 'bold',
          icon: BoldIcon,
          isActive: isBoldActive,
          onClick: handleBold
        },
        {
          id: 'underline',
          icon: UnderlineIcon,
          isActive: isUnderlineActive,
          onClick: handleUnderline
        },
        {
          id: 'italic',
          icon: ItalicIcon,
          isActive: isItalicActive,
          onClick: handleItalic
        }
      ]

  const charCount = editor ? (editor.storage.characterCount?.characters() ?? editor.getText().length) : 0

  return (
    <>
      <style>
        {`
          .ProseMirror:focus {
            outline: none;
            margin: 0px;
            border: none;
          }
          .ProseMirror p {
            font: inherit;
          }
        `}
      </style>
      <div className='space-y-0.5Z'>
        {label ? (
          <p className={`whitespace-nowrap text-text-xs-medium text-zinc-500 mb-1 ${labelCustomClass}`}>{label}</p>
        ) : null}
        <div
          className={classNames(
            "relative overflow-hidden rounded-[8px] bg-white transition-colors",
            "input-outline [&:hover:not(:focus-within)]:input-outline-hover active:input-outline-active",
            "focus-within:input-outline-focus",
            disabled ? "input-outline-disabled bg-zinc-50 opacity-50" : "",
          )}
          data-disabled={disabled}
        >
          <div className='relative py-2 px-3 bg-white text-text-sm-regular' onClick={() => editor?.commands.focus()}>
            <EditorContent
              disabled={disabled}
              editor={editor}
              onFocus={handleFocus}
              style={{
                margin: '0px',
                minHeight: showToolbar ? '32px' : '20px',
                transition: 'min-height 250ms ease-in-out',
                alignContent: 'center',
                backgroundColor: 'white'
              }}
              spellCheck={false}
            />
            {editor && editor.getText().length < 1 && placeholder ? (
              <span
                className='absolute left-2 text-text-sm-regular text-zinc-400 px-2 transition-[top] duration-[250ms] ease-in-out'
                style={{ top: showToolbar ? '14px' : '8px' }}
              >
                {placeholder}
              </span>
            ) : null}
          </div>

          <div
            className={`grid transition-[grid-template-rows] duration-[250ms] ease-in-out ${
              showToolbar && (showFormatting || Boolean(charLimit)) ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            }`}
          >
            <div className='overflow-hidden'>
              <div className='flex items-center justify-between py-2 px-3 bg-white'>
                <div className='flex gap-2'>
                  {formattingButtons.map(button => (
                    <button
                      key={button.id}
                      type='button'
                      className={`p-1 flex justify-center items-center rounded cursor-pointer hover:bg-zinc-950/6 ${
                        button.isActive ? 'bg-zinc-950/12' : 'bg-white'
                      }`}
                      disabled={disabled}
                      onClick={button.onClick}
                    >
                      <button.icon className='w-4 h-4 stroke-[2.5px] text-gray-dark' />
                    </button>
                  ))}
                </div>
                {charLimit ? (
                  <div className='text-text-xs-regular text-zinc-400'>{`${charCount}/${charLimit}`}</div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default RichTextEditor
